/**
 * Tags filter component with checkboxes
 */

'use client';

import { Check } from 'lucide-react';
import { cn } from '../../lib/utils';

interface FilterTagsProps {
  tags: Array<{ id: string; name: string }>;
  selectedTags: string[];
  onToggleTag: (tagId: string) => void;
  onClearSelection: () => void;
  filterMode: 'OR' | 'AND';
  onFilterModeChange: (mode: 'OR' | 'AND') => void;
}

export function FilterTags({
  tags,
  selectedTags,
  onToggleTag,
  onClearSelection,
  filterMode,
  onFilterModeChange,
}: FilterTagsProps) {
  const hasSelection = selectedTags.length > 0;

  if (tags.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">Tags</label>
        {hasSelection && (
          <button
            type="button"
            onClick={onClearSelection}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Clear
          </button>
        )}
      </div>

      {/* Filter Mode Toggle */}
      {hasSelection && (
        <div className="flex gap-2 mb-2">
          <button
            type="button"
            onClick={() => onFilterModeChange('OR')}
            className={cn(
              'text-xs px-2 py-1 rounded',
              filterMode === 'OR'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            )}
          >
            Any
          </button>
          <button
            type="button"
            onClick={() => onFilterModeChange('AND')}
            className={cn(
              'text-xs px-2 py-1 rounded',
              filterMode === 'AND'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            )}
          >
            All
          </button>
        </div>
      )}

      {/* Tags List */}
      <div className="space-y-1 max-h-[300px] overflow-y-auto">
        {tags.map(tag => {
          const isSelected = selectedTags.includes(tag.id);
          return (
            <label
              key={tag.id}
              className={cn(
                'flex cursor-pointer items-center gap-3 rounded-md border p-3 transition-colours',
                isSelected ? 'border-primary bg-primary/5' : 'hover:bg-accent'
              )}
            >
              <div
                className={cn(
                  'flex h-4 w-4 items-center justify-center rounded-sm border',
                  isSelected
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-input'
                )}
              >
                {isSelected && <Check className="h-3 w-3" />}
              </div>
              <span className="text-sm flex-1">{tag.name}</span>
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onToggleTag(tag.id)}
                className="sr-only"
              />
            </label>
          );
        })}
      </div>
    </div>
  );
}

